import { useEffect, useMemo, useRef, useState } from 'react';
import { Website } from '../types/website';
import { Icon, Popover } from './ui';

interface WebsiteDropdownProps {
  websites: Pick<Website, 'id' | 'url' | 'title'>[];
  selectedId: string;
  onChange: (id: string) => void;
  formatUrl: (url: string) => string;
}

/**
 * Searchable website picker on the ui/Dropdown pattern —
 * raised popover, ck-menu-pop entry, inline filter for long site lists.
 */
export default function WebsiteDropdown({ websites, selectedId, onChange, formatUrl }: WebsiteDropdownProps) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  const options = useMemo(
    () =>
      websites
        .map(site => ({ value: site.id, label: site.title || formatUrl(site.url) }))
        .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase())),
    [websites, formatUrl]
  );

  const selected = options.find(opt => opt.value === selectedId);

  const filtered = options.filter(opt =>
    opt.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Reset the filter each time the menu opens.
  useEffect(() => {
    if (open) setSearchTerm('');
  }, [open]);

  const pick = (value: string) => {
    onChange(value);
    setOpen(false);
  };

  return (
    <div ref={ref} style={{ position: 'relative', width: '100%' }}>
      <button
        type="button"
        className="pressable"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '10px',
          width: '100%',
          height: 'var(--control-md)',
          padding: '0 11px 0 12px',
          background: 'var(--surface-raised)',
          border: '1px solid ' + (open ? 'var(--accent)' : 'var(--border-hairline)'),
          borderRadius: 'var(--radius-md)',
          color: selected ? 'var(--text-body)' : 'var(--text-faint)',
          fontFamily: 'var(--font-ui)',
          fontSize: 'var(--text-sm)',
          cursor: 'pointer',
          textAlign: 'left',
          whiteSpace: 'nowrap',
          transition: 'border-color var(--dur-base) var(--ease-out)',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {selected ? selected.label : 'Select website...'}
        </span>
        <Icon
          name="chevron-down"
          size={12}
          style={{
            color: 'var(--text-faint)',
            flexShrink: 0,
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform var(--dur-base) var(--ease-out)',
          }}
        />
      </button>

      <Popover
        open={open}
        anchorEl={ref.current}
        onClose={() => setOpen(false)}
        matchWidth
        role="listbox"
        style={{ padding: 0, overflow: 'hidden' }}
      >
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search websites..."
            autoFocus
            style={{
              width: '100%',
              height: 'var(--control-sm)',
              padding: '0 12px',
              background: 'transparent',
              border: 'none',
              borderBottom: '1px solid var(--border-hairline)',
              outline: 'none',
              fontFamily: 'var(--font-ui)',
              fontSize: 'var(--text-sm)',
              color: 'var(--text-body)',
            }}
          />
          <div style={{ maxHeight: '240px', overflowY: 'auto', padding: '4px' }}>
            {filtered.length === 0 ? (
              <div style={{ padding: '7px 10px', fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>
                No websites found
              </div>
            ) : (
              filtered.map(opt => {
                const isSel = opt.value === selectedId;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="option"
                    aria-selected={isSel}
                    className="hover-wash pressable"
                    onClick={() => pick(opt.value)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '9px',
                      width: '100%',
                      padding: '7px 10px',
                      border: 'none',
                      textAlign: 'left',
                      background: isSel ? 'var(--surface-active)' : 'transparent',
                      borderRadius: 'var(--radius-sm)',
                      cursor: 'pointer',
                      fontFamily: 'var(--font-ui)',
                      fontSize: 'var(--text-sm)',
                      color: 'var(--text-body)',
                    }}
                  >
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {opt.label}
                    </span>
                    {isSel && <Icon name="check" size={12} style={{ color: 'var(--accent-text)' }} />}
                  </button>
                );
              })
            )}
          </div>
      </Popover>
    </div>
  );
}
